import { useCallback, useEffect, useState } from 'react';
import { api, type Performance, type Production, type RosterMember, type ShowSummary } from '../lib/api';
import { useStore } from '../state/store';
import ConfirmDialog from './ConfirmDialog';
import ProfilePhoto from './ProfilePhoto';
import Account from './Account';
import CastEditor from './CastEditor';
import Avatar from './Avatar';

type Screen =
  | { name: 'productions' }
  | { name: 'production'; id: string }
  | { name: 'trash' }
  | { name: 'shows' };

/**
 * Everything a signed-in producer sees before opening a board.
 *
 * Productions are the spine: a production owns a cast and outlives any single
 * night. Standalone shows still exist for one-off work and for anything created
 * before productions did.
 */
export default function Library() {
  const user = useStore(s => s.user)!;
  const signOut = useStore(s => s.signOut);
  const notify = useStore(s => s.notify);
  const setIdentity = useStore(s => s.setIdentity);

  const [screen, setScreen] = useState<Screen>({ name: 'productions' });
  const [accountOpen, setAccountOpen] = useState(false);

  return (
    <div className="app">
      <header className="header">
        <div className="brand">mic<span>Plot</span></div>
        <nav className="nav">
          <button aria-current={screen.name === 'productions' || screen.name === 'production'}
                  onClick={() => setScreen({ name: 'productions' })}>Productions</button>
          <button aria-current={screen.name === 'shows'}
                  onClick={() => setScreen({ name: 'shows' })}>One-off shows</button>
          <button aria-current={screen.name === 'trash'}
                  onClick={() => setScreen({ name: 'trash' })}>Trash</button>
        </nav>
        <div className="header-spacer" />
        <ProfilePhoto />
        <button className="btn sm" onClick={() => setAccountOpen(true)}>
          {user.displayName}
        </button>
        <button className="btn sm" onClick={() => signOut()}>Sign out</button>
      </header>

      <main className="work" style={{ overflowY: 'auto' }}>
        <div className="library">
          {screen.name === 'productions' && (
            <ProductionList
              onOpen={id => setScreen({ name: 'production', id })}
              notify={notify}
            />
          )}
          {screen.name === 'production' && (
            <ProductionDetail
              id={screen.id}
              onBack={() => setScreen({ name: 'productions' })}
              notify={notify}
              setIdentity={setIdentity}
              userName={user.displayName}
            />
          )}
          {screen.name === 'trash' && <Trash notify={notify} />}
          {screen.name === 'shows' && (
            <StandaloneShows notify={notify} setIdentity={setIdentity} userName={user.displayName} />
          )}
        </div>
      </main>

      {accountOpen && <Account onClose={() => setAccountOpen(false)} />}
    </div>
  );
}

type Notify = (t: string, k?: 'info' | 'error') => void;

// -------------------------------------------------------------- productions

function ProductionList({ onOpen, notify }: { onOpen: (id: string) => void; notify: Notify }) {
  const [items, setItems] = useState<Production[] | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.listProductions().then(setItems).catch(e => {
      notify((e as Error).message, 'error');
      setItems([]);
    });
  }, [notify]);
  useEffect(load, [load]);

  return (
    <>
      <form className="panel" style={{ padding: 16, marginBottom: 16 }}
            onSubmit={async e => {
              e.preventDefault();
              if (!name.trim()) return;
              setBusy(true);
              try {
                const p = await api.createProduction(name.trim());
                setName('');
                load();
                onOpen(p.id);
              } catch (err) { notify((err as Error).message, 'error'); }
              finally { setBusy(false); }
            }}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="pname">New production</label>
          <div className="row">
            <input id="pname" value={name} onChange={e => setName(e.target.value)}
                   placeholder="e.g. Hamlet — spring run" />
            <button className="btn primary" disabled={busy || !name.trim()}>Create</button>
          </div>
          <div className="hint">
            A production holds its cast and every performance. Set it up once and
            reopen it each night.
          </div>
        </div>
      </form>

      <div className="panel">
        <div className="panel-head">
          <span className="panel-title">Your productions</span>
          <span className="count">{items?.length ?? 0}</span>
        </div>
        {items === null ? <div className="empty">Loading…</div>
          : items.length === 0 ? (
            <div className="empty">
              <div>No productions yet.</div>
              <div style={{ fontSize: 12 }}>Create one above to start building a cast.</div>
            </div>
          ) : (
            <div style={{ padding: 10, display: 'grid', gap: 8 }}>
              {items.map(p => (
                <button key={p.id} className="show-row" onClick={() => onOpen(p.id)}>
                  <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <div className="show-title">{p.name}</div>
                    <div className="stage-meta">
                      {p.cast} in the cast · {p.performances} performance
                      {p.performances === 1 ? '' : 's'}
                      {!p.isCreator && ' · shared with you'}
                    </div>
                  </span>
                  <span className="muted" style={{ fontSize: 12 }}>{relative(p.updatedAt)}</span>
                </button>
              ))}
            </div>
          )}
      </div>
    </>
  );
}

// ------------------------------------------------------------------ detail

interface DetailProps {
  id: string;
  onBack: () => void;
  notify: Notify;
  setIdentity: (i: any) => void;
  userName: string;
}

function ProductionDetail({ id, onBack, notify, setIdentity, userName }: DetailProps) {
  const [data, setData] = useState<{
    production: Production; roster: RosterMember[]; performances: Performance[];
  } | null>(null);
  const [editing, setEditing] = useState<RosterMember | null>(null);
  const [confirm, setConfirm] = useState<null | 'trash'>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [perfTitle, setPerfTitle] = useState('');

  const load = useCallback(() => {
    api.getProduction(id).then(setData).catch(e => notify((e as Error).message, 'error'));
  }, [id, notify]);
  useEffect(load, [load]);

  if (!data) return <div className="empty">Loading…</div>;
  const { production, roster, performances } = data;

  const run = async (fn: () => Promise<unknown>) => {
    try { await fn(); load(); } catch (e) { notify((e as Error).message, 'error'); }
  };

  async function openPerformance(code: string) {
    try {
      const o = await api.openShow(code);
      setIdentity({ code, participantId: o.participantId, role: 'host', name: userName });
    } catch (e) { notify((e as Error).message, 'error'); }
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 12 }}>
        <button className="btn sm" onClick={onBack}>‹ All productions</button>
        <div className="header-spacer" />
        <button className="btn sm" onClick={() => setRenaming(production.name)}>Rename</button>
        <button className="btn sm" onClick={() => run(async () => {
          const copy = await api.duplicateProduction(production.id);
          notify(`Duplicated as "${copy.name}".`);
        })}>Duplicate</button>
        <button className="btn sm danger" onClick={() => setConfirm('trash')}>Move to trash</button>
      </div>

      <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>{production.name}</h1>
      <p className="sub" style={{ marginBottom: 18 }}>
        {roster.length} in the cast · {performances.length} performance
        {performances.length === 1 ? '' : 's'}
      </p>

      {/* ---- performances ---- */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-head">
          <span className="panel-title">Performances</span>
          <div className="header-spacer" />
          <input value={perfTitle} onChange={e => setPerfTitle(e.target.value)}
                 placeholder="e.g. Opening night" style={{ width: 180 }} />
          <button className="btn sm primary" onClick={() => run(async () => {
            const r = await api.startPerformance(production.id, perfTitle.trim() || undefined);
            setPerfTitle('');
            setIdentity({
              code: r.session.code, participantId: r.participantId,
              role: 'host', name: userName,
            });
          })}>
            Start a night
          </button>
        </div>
        {performances.length === 0 ? (
          <div className="empty">
            <div>No performances yet.</div>
            <div style={{ fontSize: 12 }}>
              Starting a night copies the cast onto a fresh board.
            </div>
          </div>
        ) : (
          <div style={{ padding: 10, display: 'grid', gap: 8 }}>
            {performances.map(p => (
              <button key={p.code} className="show-row" onClick={() => openPerformance(p.code)}>
                <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <div className="show-title">{p.title}</div>
                  <div className="stage-meta">
                    {p.presenters} presenters · {p.channels} channels ·{' '}
                    {relative(p.updatedAt)}{p.endedAt ? ' · ended' : ''}
                  </div>
                </span>
                <span className="code-pill">{p.code}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ---- cast ---- */}
      <div className="panel">
        <div className="panel-head">
          <span className="panel-title">Cast</span>
          <span className="count">{roster.length}</span>
          <div className="header-spacer" />
          <button className="btn sm" onClick={() => run(async () => {
            await api.addCast(production.id, { name: `Cast ${roster.length + 1}` });
          })}>+ Add</button>
        </div>
        {roster.length === 0 ? (
          <div className="empty">
            <div>No cast yet.</div>
            <div style={{ fontSize: 12 }}>
              Add people once here and every performance starts with them.
            </div>
          </div>
        ) : (
          <div style={{ padding: 10, display: 'grid', gap: 7 }}>
            {roster.map(m => (
              <div className="cast-row" key={m.id}>
                <Avatar presenter={m as any} size={34} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div className="show-title">{m.name}</div>
                  {(m.characterName || m.title) && (
                    <div className="stage-meta">{m.characterName || m.title}</div>
                  )}
                </span>
                <span className="chip">{m.micPreference}</span>
                <button className="btn sm" onClick={() => setEditing(m)}>Edit</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <CastEditor
          productionId={production.id}
          member={editing}
          onClose={() => { setEditing(null); load(); }}
          notify={notify}
        />
      )}

      {renaming !== null && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setRenaming(null)}>
          <div className="modal" style={{ maxWidth: 420 }} role="dialog" aria-label="Rename production">
            <h2>Rename production</h2>
            <div className="field">
              <label htmlFor="rename">Name</label>
              <input id="rename" value={renaming} autoFocus
                     onChange={e => setRenaming(e.target.value)}
                     onKeyDown={e => { if (e.key === 'Enter' && renaming.trim()) {
                       const name = renaming.trim();
                       setRenaming(null);
                       void run(() => api.renameProduction(production.id, name));
                     } }} />
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => setRenaming(null)}>Cancel</button>
              <button className="btn primary" disabled={!renaming.trim()}
                      onClick={() => {
                        const name = renaming.trim();
                        setRenaming(null);
                        void run(() => api.renameProduction(production.id, name));
                      }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {confirm === 'trash' && (
        <ConfirmDialog
          title={`Move "${production.name}" to the trash?`}
          body="Its cast and performances go with it. You can restore it from Trash for 30 days, after which it is deleted permanently."
          confirmLabel="Move to trash"
          danger
          onConfirm={async () => {
            setConfirm(null);
            try {
              await api.trashProduction(production.id);
              notify('Moved to trash. Recoverable for 30 days.');
              onBack();
            } catch (e) { notify((e as Error).message, 'error'); }
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
}

// ------------------------------------------------------------------- trash

function Trash({ notify }: { notify: Notify }) {
  const [items, setItems] = useState<Production[] | null>(null);
  const [purging, setPurging] = useState<Production | null>(null);

  const load = useCallback(() => {
    api.listTrash().then(setItems).catch(e => {
      notify((e as Error).message, 'error');
      setItems([]);
    });
  }, [notify]);
  useEffect(load, [load]);

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="panel-title">Trash</span>
        <span className="count">{items?.length ?? 0}</span>
        <div className="header-spacer" />
        <span className="muted" style={{ fontSize: 12 }}>
          Emptied automatically after 30 days
        </span>
      </div>
      {items === null ? <div className="empty">Loading…</div>
        : items.length === 0 ? <div className="empty">Nothing in the trash.</div>
          : (
            <div style={{ padding: 10, display: 'grid', gap: 8 }}>
              {items.map(p => (
                <div className="cast-row" key={p.id}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <div className="show-title">{p.name}</div>
                    <div className="stage-meta">
                      {p.cast} cast · {p.performances} performances ·{' '}
                      deletes in {p.purgesInDays} day{p.purgesInDays === 1 ? '' : 's'}
                    </div>
                  </span>
                  <button className="btn sm" onClick={async () => {
                    try { await api.restoreProduction(p.id); notify('Restored.'); load(); }
                    catch (e) { notify((e as Error).message, 'error'); }
                  }}>Restore</button>
                  <button className="btn sm danger" onClick={() => setPurging(p)}>
                    Delete now
                  </button>
                </div>
              ))}
            </div>
          )}

      {purging && (
        <ConfirmDialog
          title={`Permanently delete "${purging.name}"?`}
          body="This cannot be undone. The cast, their photos and every performance are erased."
          confirmLabel="Delete permanently"
          danger
          onConfirm={async () => {
            const p = purging;
            setPurging(null);
            try { await api.purgeProduction(p.id); notify('Deleted.'); load(); }
            catch (e) { notify((e as Error).message, 'error'); }
          }}
          onCancel={() => setPurging(null)}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------- standalone shows

function StandaloneShows(
  { notify, setIdentity, userName }:
  { notify: Notify; setIdentity: (i: any) => void; userName: string },
) {
  const [shows, setShows] = useState<ShowSummary[] | null>(null);
  const [title, setTitle] = useState('');

  useEffect(() => {
    api.listShows().then(setShows).catch(e => {
      notify((e as Error).message, 'error');
      setShows([]);
    });
  }, [notify]);

  return (
    <>
      <form className="panel" style={{ padding: 16, marginBottom: 16 }}
            onSubmit={async e => {
              e.preventDefault();
              try {
                const r = await api.createSession(userName, title.trim() || undefined);
                setIdentity({
                  code: r.session.code, participantId: r.participantId,
                  role: 'host', name: userName,
                });
              } catch (err) { notify((err as Error).message, 'error'); }
            }}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="stitle">One-off show</label>
          <div className="row">
            <input id="stitle" value={title} onChange={e => setTitle(e.target.value)}
                   placeholder="e.g. Tuesday corporate panel" />
            <button className="btn primary">Create</button>
          </div>
          <div className="hint">
            For work that will not repeat. Anything you will remount belongs in a production.
          </div>
        </div>
      </form>

      <div className="panel">
        <div className="panel-head">
          <span className="panel-title">All shows</span>
          <span className="count">{shows?.length ?? 0}</span>
        </div>
        {shows === null ? <div className="empty">Loading…</div>
          : shows.length === 0 ? <div className="empty">No shows yet.</div>
            : (
              <div style={{ padding: 10, display: 'grid', gap: 8 }}>
                {shows.map(s => (
                  <button key={s.code} className="show-row" onClick={async () => {
                    try {
                      const o = await api.openShow(s.code);
                      setIdentity({
                        code: s.code, participantId: o.participantId,
                        role: 'host', name: userName,
                      });
                    } catch (e) { notify((e as Error).message, 'error'); }
                  }}>
                    <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                      <div className="show-title">{s.title}</div>
                      <div className="stage-meta">
                        {s.presenters} presenters · {s.channels} channels ·{' '}
                        {relative(s.updatedAt)}
                      </div>
                    </span>
                    <span className="code-pill">{s.code}</span>
                  </button>
                ))}
              </div>
            )}
      </div>
    </>
  );
}

function relative(ts: number): string {
  const mins = Math.round((Date.now() - ts) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}
